import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { useOrganization } from "@/auth/organization";

import { portal } from "./api";
import type { FormDraft } from "./types";

/** How long typing must pause before the draft is saved. */
export const DRAFT_SAVE_DELAY_MS = 1500;

export interface FormDraftApi<T> {
  /** A saved draft that differs from the form, until the person continues it or starts fresh. */
  offer: FormDraft<T> | null;
  /** Continue the draft: returns its values for the form to apply. */
  restore: () => T | null;
  /** Throw the draft away and keep the form as it is. */
  startFresh: () => void;
  /** When the current values were last saved, for "Draft saved 1 min ago". */
  savedAt: string | null;
  /** The form was sent: forget the draft (and keep watching for new changes from here). */
  clear: () => void;
}

/**
 * Keeps a half-filled form on the server (per person, business and form) so the person can continue where they left
 * off, on any device. Saves a moment after typing stops, and only once the values differ from where the form started.
 *
 * `ready`: the form's starting values are in place (a form that can't be edited passes false). Leave sensitive
 * fields (passwords, codes, card details) out of `value`; the server drops them too.
 */
export function useFormDraft<T>(
  form: string,
  value: T,
  { ready = true }: { ready?: boolean } = {},
): FormDraftApi<T> {
  const { orgId } = useOrganization();
  const draft = useQuery({
    queryKey: ["form-draft", orgId, form],
    queryFn: () => portal.formDraft<T>(orgId, form),
    enabled: Boolean(orgId) && ready,
    staleTime: Infinity,
    // A form opened again on the same page must ask the server again: the answer cached from the last time
    // (often "no draft", from before the person typed) is out of date.
    refetchOnMount: "always",
    gcTime: 0,
    retry: false,
  });

  const current = JSON.stringify(value);
  // Where the form started (or was last sent from): only a change from here is worth keeping.
  const [baseline, setBaseline] = useState<string | null>(ready ? current : null);
  if (ready && baseline === null) setBaseline(current);

  const [decided, setDecided] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const saving = useRef<Promise<unknown>>(Promise.resolve());

  const stored = draft.data ?? null;
  const offer =
    !decided && stored && baseline !== null && JSON.stringify(stored.data) !== baseline ? stored : null;
  // Wait for the lookup and the person's answer before saving over a draft they may still want.
  const settled = ready && (decided || draft.isFetched || draft.isError) && !offer;

  useEffect(() => {
    if (!settled || !orgId || current === baseline) return;
    timer.current = setTimeout(() => {
      saving.current = portal
        .saveFormDraft(orgId, form, JSON.parse(current) as T)
        .then((d) => setSavedAt(d.saved_at))
        .catch(() => undefined); // a draft that could not be kept never gets in the way of the form
    }, DRAFT_SAVE_DELAY_MS);
    return () => clearTimeout(timer.current);
  }, [settled, orgId, form, current, baseline]);

  const discard = () => {
    clearTimeout(timer.current);
    setSavedAt(null);
    // After any save still on its way, so it cannot bring the draft back.
    void saving.current.then(() => portal.discardFormDraft(orgId, form).catch(() => undefined));
  };

  return {
    offer,
    savedAt,
    restore: () => {
      setDecided(true);
      return stored?.data ?? null;
    },
    startFresh: () => {
      setDecided(true);
      discard();
    },
    clear: () => {
      setDecided(true);
      setBaseline(current);
      discard();
    },
  };
}
